import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    ActivityIndicator,
    Pressable,
    ScrollView,
    StyleSheet,
    Text,
    View,
} from "react-native";
import LinearGradient from "react-native-linear-gradient";
import { DateTimePickerAndroid } from "@react-native-community/datetimepicker";
import { format } from "date-fns";
import { useFocusEffect, useNavigation } from "@react-navigation/native";

import { DivineBackground } from "../../../shared/ui/DivineBackground";
import { Header } from "../../../shared/ui/Header";
import { Input } from "../../../shared/ui/FormElements";
import { Flash } from "../../../shared/utils/Flash";
import { useAuth } from "../../../../context/AuthContext";
import { TimeBasedService } from "../../../services/TimeBasedService";

type EnumItem = {
    enumerationId: string;
    code: string;
    value: string;
    sequence: number;
    parameter?: string | number | null;
};

type TotalWorshipsModel = {
    totalMinutes: number;
    totalMarks: number;
    minutesOfWorships: string;
    marksOfWorships: number;
    readingContent?: string | null;
    existingEnumValue?: number | null;
    isEditable: boolean;
};

const MIN_REQUIRED_CHARS = 10;
const CHARS_PER_BLOCK = 10;
const MARKS_PER_BLOCK = 5;

const timeOnlyToMinutes = (time: string | null | undefined) => {
    if (!time) return 0;

    const parts = time.split(":");
    if (parts.length < 2) return 0;

    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;

    return hours * 60 + minutes;
};

const onlyDigits = (s: string) => (s || "").replace(/[^\d]/g, "");

const minutesToTimeOnlyString = (mins: number) => {
    const safe = Number.isFinite(mins) && mins >= 0 ? Math.floor(mins) : 0;
    const h = Math.floor(safe / 60);
    const m = safe % 60;

    const hh = String(h).padStart(2, "0");
    const mm = String(m).padStart(2, "0");

    return `${hh}:${mm}:00`;
};

const getEffectiveCharCount = (text: string) => {
    return (text || "").trim().length;
};

const calculateOtherSevaMarks = (text: string) => {
    const charCount = getEffectiveCharCount(text);

    if (charCount < MIN_REQUIRED_CHARS) return 0;

    const blocks = Math.floor(charCount / CHARS_PER_BLOCK);
    return MARKS_PER_BLOCK;
};

export function OtherSevaScreen() {
    const navigation = useNavigation<any>();
    const { user } = useAuth();

    const todayStr = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
    const [date, setDate] = useState(todayStr);

    const [loading, setLoading] = useState(false);

    const [worshipTypeId, setWorshipTypeId] = useState<string | null>(null);
    const [worshipTypeLabel, setWorshipTypeLabel] = useState<string>("Other Seva");

    const [minutes, setMinutes] = useState<string>("0");
    const [otherSevaContent, setOtherSevaContent] = useState<string>("");

    const minutesNum = useMemo(() => {
        const n = parseInt(minutes || "0", 10);
        return Number.isFinite(n) && n >= 0 ? n : 0;
    }, [minutes]);

    const effectiveCharCount = useMemo(() => {
        return getEffectiveCharCount(otherSevaContent);
    }, [otherSevaContent]);

    const calculatedMarks = useMemo(() => {
        return calculateOtherSevaMarks(otherSevaContent);
    }, [otherSevaContent]);

    const isOtherSevaValid = effectiveCharCount >= MIN_REQUIRED_CHARS;
    const isSaveDisabled = loading || !isOtherSevaValid || calculatedMarks < 5;

    const [totalMinutes, setTotalMinutes] = useState(0);
    const [totalMarks, setTotalMarks] = useState(0);
    const [isEditable, setIsEditable] = useState(true);

    const marksInfoText = useMemo(() => "Every 10 characters = 5 Marks", []);
    const marksRuleText = useMemo(
        () => "10-19 chars = 5, 20-29 chars = 10, 30-39 chars = 15",
        []
    );

    const openDatePicker = () => {
        DateTimePickerAndroid.open({
            value: new Date(date),
            mode: "date",
            maximumDate: new Date(),
            onChange: (event, selectedDate) => {
                if (event.type === "set" && selectedDate) {
                    setDate(format(selectedDate, "yyyy-MM-dd"));
                }
            },
        });
    };

    const loadEnumerationAndSetOtherSeva = useCallback(async () => {
        try {
            setLoading(true);

            const res = await TimeBasedService.getEnumeration("WorshipType");
            if (res?.status !== 200) {
                Flash.error(res?.message || "Failed to load worship types");
                return;
            }

            const list = (res?.data || []) as EnumItem[];

            
            const otherSeva = list.find(
                (x) => (x.value || "").trim().toLowerCase() === "other"
            );

            if (!otherSeva?.enumerationId) {
                Flash.error("Other Seva worship type not found in Enumeration");
                return;
            }

            setWorshipTypeId(otherSeva.enumerationId);
            setWorshipTypeLabel(otherSeva.value || "Other Seva");
        } catch (e: any) {
            Flash.error(e?.message || "Enumeration load error");
        } finally {
            setLoading(false);
        }
    }, []);

    const loadAgainstDate = useCallback(async (dateStr: string, wTypeId: string) => {
        try {
            setLoading(true);

            const res = await TimeBasedService.getWorshipAgainstDate({
                date: dateStr,
                worshipType: wTypeId,
            });

            if (res?.status !== 200) {
                Flash.error(res?.message || "Failed to load other seva data");
                return;
            }

            const data = (res?.data || null) as TotalWorshipsModel | null;

            setTotalMinutes(data?.totalMinutes ?? 0);
            setTotalMarks(data?.totalMarks ?? 0);
            setIsEditable(data?.isEditable ?? true);

            const mins = timeOnlyToMinutes(data?.minutesOfWorships);
            setMinutes(String(mins));
            setOtherSevaContent((data?.readingContent ?? "") as string);
        } catch (e: any) {
            Flash.error(e?.message || "Load failed");
        } finally {
            setLoading(false);
        }
    }, []);

    const init = useCallback(async () => {
        await loadEnumerationAndSetOtherSeva();
    }, [loadEnumerationAndSetOtherSeva]);

    useEffect(() => {
        init();
    }, [init]);

    useFocusEffect(
        useCallback(() => {
            if (!worshipTypeId) return;
            loadAgainstDate(date, worshipTypeId);
        }, [date, worshipTypeId, loadAgainstDate])
    );

    const onSave = async () => {
        try {
            if (!user?.userId) {
                Flash.error("User not found");
                return;
            }

            if (!worshipTypeId) {
                Flash.error("Worship type not ready");
                return;
            }

            if (!isOtherSevaValid) {
                Flash.error("Minimum 10 characters required.");
                return;
            }

            if (calculatedMarks < 5) {
                Flash.error("Minimum 5 marks required to save.");
                return;
            }

            setLoading(true);

            const minutesTimeOnly = minutesToTimeOnlyString(1);

            const payload = {
                worshipId: null,
                userId: user.userId,
                date,
                worshipType: worshipTypeId,
                minutes: minutesTimeOnly,
                marks: calculatedMarks,
                readingContent: otherSevaContent?.trim() ? otherSevaContent.trim() : null,
            };
            
            const res = await TimeBasedService.create(payload);

            if (res?.status !== 200) {
                Flash.error(res?.message || "Failed to save");
                return;
            }

            Flash.success("Success!", "Other Seva Saved Successfully!");
            navigation.navigate("OtherSevaHistory");
        } catch (e: any) {
            Flash.error(e?.message || "Save failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <DivineBackground overlayOpacity={0.92}>
            <View style={{ flex: 1 }}>
                <Header title={worshipTypeLabel} showBack />

                <ScrollView
                    contentContainerStyle={styles.container}
                    showsVerticalScrollIndicator={false}
                >
                    <View style={styles.summaryCard}>
                        <View style={styles.summaryTopRow}>
                            <Text style={styles.summaryTitle}>This Month&apos;s Offering</Text>

                            <View style={styles.summaryRightBox}>
                                <Text style={styles.summaryMini}>TOTAL MARKS</Text>
                                <Text style={styles.summaryValue}>{totalMarks}</Text>
                            </View>
                        </View>
                    </View>

                    <View style={styles.card}>
                        <View style={styles.topRow}>
                            <Pressable onPress={openDatePicker} style={styles.dateChip}>
                                <Text style={styles.dateChipIcon}>📅</Text>
                                <Text style={styles.dateChipText}>
                                    {format(new Date(date), "dd-MM-yyyy")}
                                </Text>
                            </Pressable>

                            <Pressable
                                onPress={() => navigation.navigate("OtherSevaHistory")}
                                style={styles.historyBtn}
                            >
                                <Text style={styles.historyIcon}>🕘</Text>
                                <Text style={styles.historyText}>History</Text>
                            </Pressable>
                        </View>

                        <View style={{ marginTop: 14, gap: 10 }}>

                            <Input
                                label="Other Seva Details *"
                                value={otherSevaContent}
                                onChangeText={(text) => {
                                    if (text.length <= 1000) {
                                        setOtherSevaContent(text);
                                    }
                                }}
                                placeholder="Describe your seva..."
                                multiline
                                numberOfLines={4}
                                textAlignVertical="top"
                                maxLength={1000}
                                containerStyle={{ gap: 8 }}
                            />

                            {!isOtherSevaValid ? (
                                <Text style={styles.validationText}>
                                    Minimum 10 characters required.
                                </Text>
                            ) : null}

                            <View style={styles.calcRow}>
                                <Text style={styles.calcLabel}>Calculated Marks:</Text>
                                <Text style={styles.calcValue}>{calculatedMarks}</Text>
                            </View>

                            {isEditable && (
                                <View style={styles.btnRow}>
                                    <Pressable
                                        onPress={() => navigation.goBack()}
                                        disabled={loading}
                                        style={({ pressed }) => [
                                            styles.cancelBtn,
                                            pressed ? { opacity: 0.95 } : null,
                                            loading ? { opacity: 0.6 } : null,
                                        ]}
                                    >
                                        <Text style={styles.cancelText}>Cancel</Text>
                                    </Pressable>

                                    <Pressable
                                        disabled={isSaveDisabled}
                                        onPress={onSave}
                                        style={{ flex: 1 }}
                                    >
                                        <LinearGradient
                                            colors={["#2563EB", "#7C3AED"]}
                                            start={{ x: 0, y: 0.5 }}
                                            end={{ x: 1, y: 0.5 }}
                                            style={[
                                                styles.saveBtn,
                                                isSaveDisabled ? { opacity: 0.75 } : null,
                                            ]}
                                        >
                                            <Text style={styles.saveText}>
                                                {loading ? "Saving..." : "Save"}
                                            </Text>
                                        </LinearGradient>
                                    </Pressable>
                                </View>
                            )}

                            {loading ? (
                                <View style={{ marginTop: 10, alignItems: "center" }}>
                                    <ActivityIndicator />
                                </View>
                            ) : null}
                        </View>
                    </View>
                </ScrollView>
            </View>
        </DivineBackground>
    );
}

const styles = StyleSheet.create({
    container: {
        padding: 14,
        paddingBottom: 30,
        gap: 12,
    },

    summaryCard: {
        borderRadius: 14,
        padding: 14,
        backgroundColor: "rgba(255,255,255,0.82)",
        borderWidth: 1,
        borderColor: "rgba(37,99,235,0.15)",
        shadowColor: "#000",
        shadowOpacity: 0.08,
        shadowRadius: 12,
        shadowOffset: { width: 0, height: 8 },
        elevation: 3,
    },

    summaryTopRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
    },

    summaryRightBox: {
        alignItems: "flex-end",
    },

    summaryTitle: {
        fontSize: 13,
        fontWeight: "900",
        color: "rgba(17,24,39,0.85)",
    },

    summarySplit: {
        flexDirection: "row",
        alignItems: "center",
        marginTop: 10,
    },

    summaryMini: {
        fontSize: 10,
        fontWeight: "900",
        color: "rgba(0,0,0,0.35)",
        letterSpacing: 0.7,
    },

    summaryValue: {
        marginTop: 4,
        fontSize: 20,
        fontWeight: "900",
        color: "#1D4ED8",
    },

    divider: {
        width: 1,
        height: 40,
        backgroundColor: "rgba(0,0,0,0.06)",
    },

    card: {
        borderRadius: 16,
        padding: 14,
        backgroundColor: "rgba(255,255,255,0.90)",
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.06)",
        shadowColor: "#000",
        shadowOpacity: 0.1,
        shadowRadius: 14,
        shadowOffset: { width: 0, height: 10 },
        elevation: 4,
    },

    topRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },

    dateChip: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        backgroundColor: "rgba(37,99,235,0.08)",
        borderWidth: 1,
        borderColor: "rgba(37,99,235,0.18)",
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 999,
    },

    dateChipIcon: {
        fontSize: 14,
        opacity: 0.9,
    },

    dateChipText: {
        fontSize: 13,
        fontWeight: "800",
        color: "rgba(17,24,39,0.85)",
    },

    validationText: {
        marginTop: -4,
        fontSize: 11,
        fontWeight: "800",
        color: "#DC2626",
    },

    helperText: {
        marginTop: -2,
        fontSize: 11,
        fontWeight: "800",
        color: "rgba(17,24,39,0.55)",
    },

    ruleText: {
        marginTop: -2,
        fontSize: 11,
        fontWeight: "700",
        color: "rgba(17,24,39,0.45)",
    },

    charCount: {
        fontSize: 11,
        color: "rgba(0,0,0,0.4)",
        textAlign: "right",
    },

    calcRow: {
        borderRadius: 14,
        paddingHorizontal: 12,
        paddingVertical: 10,
        backgroundColor: "rgba(37,99,235,0.06)",
        borderWidth: 1,
        borderColor: "rgba(37,99,235,0.12)",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },

    calcLabel: {
        fontSize: 13,
        fontWeight: "800",
        color: "rgba(17,24,39,0.60)",
    },

    calcValue: {
        fontSize: 18,
        fontWeight: "900",
        color: "#1D4ED8",
    },

    btnRow: {
        flexDirection: "row",
        gap: 10,
        marginTop: 6,
    },

    cancelBtn: {
        flex: 1,
        height: 46,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.10)",
        backgroundColor: "rgba(255,255,255,0.95)",
        alignItems: "center",
        justifyContent: "center",
    },

    cancelText: {
        fontSize: 14,
        fontWeight: "900",
        color: "rgba(17,24,39,0.55)",
    },

    saveBtn: {
        height: 46,
        borderRadius: 10,
        alignItems: "center",
        justifyContent: "center",
        shadowColor: "#000",
        shadowOpacity: 0.12,
        shadowRadius: 10,
        shadowOffset: { width: 0, height: 6 },
        elevation: 4,
    },

    saveText: {
        fontSize: 14,
        fontWeight: "900",
        color: "#fff",
    },

    historyBtn: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        paddingHorizontal: 10,
        height: 34,
        borderRadius: 10,
        backgroundColor: "rgba(37,99,235,0.06)",
        borderWidth: 1,
        borderColor: "rgba(37,99,235,0.12)",
    },
    historyIcon: { fontSize: 14 },
    historyText: { fontSize: 12, fontWeight: "900", color: "#1D4ED8" },
});
